import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar } from "lucide-react";
import { trackDemoClick } from "@/lib/analytics";
import { useIsMobile } from "@/hooks/use-mobile";

const SCROLL_THRESHOLD = 300;
const FOOTER_BUFFER = 200;

/**
 * StickyMobileCTA - Mobile-only sticky CTA bar
 * Appears after 300px scroll, hides near footer
 */
export function StickyMobileCTA() {
  const [isVisible, setIsVisible] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isMobile) {
      setIsVisible(false);
      return;
    }

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const documentHeight = document.documentElement.scrollHeight;
      const windowHeight = window.innerHeight;
      const distanceFromBottom = documentHeight - (scrollY + windowHeight);

      // Show after threshold, hide near footer
      const shouldShow = scrollY > SCROLL_THRESHOLD && distanceFromBottom > FOOTER_BUFFER;
      setIsVisible(shouldShow);
    };

    // Passive listener for performance
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMobile]);

  // Only render on mobile
  if (!isMobile) return null;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 md:hidden transition-transform duration-300 ease-out ${
        isVisible ? "translate-y-0" : "translate-y-full"
      }`}
    >
      <div className="bg-background/95 backdrop-blur-md border-t border-border/50 px-4 py-3 shadow-lg">
        <Button
          asChild
          className="w-full bg-ch-cyan hover:bg-ch-cyan/90 text-white font-semibold h-12 rounded-xl"
          onClick={() => trackDemoClick("sticky_mobile_cta")}
        >
          <Link to="/demo#book" className="flex items-center justify-center gap-2">
            <Calendar className="w-4 h-4" />
            Book a Demo
          </Link>
        </Button>
      </div>
    </div>
  );
}

export default StickyMobileCTA;
